import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type { SxProps, Theme } from '@mui/material/styles';

import {
  AGE_RANGES,
  ageSelectionLabel,
  resolveAgeSelection,
} from '../../../config/dropdowns';
import { selectCheckboxSx, selectMenuItemSx, selectSelectedLabelSx } from '../selectMenuItemSx';

interface AgeSelectProps {
  /** Selected age-range ids (e.g. ['all_children'] or ['under_5', '5_to_9']). */
  value: string[];
  onChange: (ids: string[]) => void;
  sx?: SxProps<Theme>;
  variant?: 'standard' | 'outlined' | 'filled';
  displayEmpty?: boolean;
  /** Shown when nothing is selected (only with displayEmpty). */
  placeholder?: string;
  id?: string;
  ariaLabel?: string;
}

/**
 * Shared multi-select for age ranges. Enforces the "all ages" ↔ specific-range
 * exclusivity and renders each row with a right-aligned checkbox — used by
 * the hero search, the madlib, and the impact map.
 */
export function AgeSelect({
  value,
  onChange,
  sx,
  variant = 'standard',
  displayEmpty,
  placeholder,
  id,
  ariaLabel,
}: AgeSelectProps) {
  return (
    <Select
      id={id}
      multiple
      value={value}
      variant={variant}
      displayEmpty={displayEmpty}
      aria-label={ariaLabel}
      onChange={(event) =>
        onChange(resolveAgeSelection(value, event.target.value as string[]))
      }
      renderValue={(v) => {
        const arr = v as string[];
        return arr.length ? ageSelectionLabel(arr) : (placeholder ?? 'All ages');
      }}
      sx={sx}
    >
      {AGE_RANGES.map((range) => {
        const selected = value.includes(range.id);
        return (
          <MenuItem key={range.id} value={range.id} sx={selectMenuItemSx}>
            <Checkbox checked={selected} size="small" sx={selectCheckboxSx} />
            <ListItemText
              primary={range.label}
              slotProps={{ primary: { sx: selected ? selectSelectedLabelSx : undefined } }}
            />
          </MenuItem>
        );
      })}
    </Select>
  );
}
